import React from 'react';
import { Box, IconButton, Slider, Stack, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrowOutlined';
import PauseIcon from '@mui/icons-material/PauseOutlined';
import Replay5Icon from '@mui/icons-material/Replay5Outlined';
import Forward5Icon from '@mui/icons-material/Forward5Outlined';
import VolumeOffIcon from '@mui/icons-material/VolumeOffOutlined';
import VolumeUpIcon from '@mui/icons-material/VolumeUpOutlined';
import type { EditList } from '../../utils/editing/editList';
import { segmentPlacements } from '../../utils/editing/editList';
import { formatMs } from './editStudioModel';
import type { PreviewPlayer } from './usePreviewPlayer';

interface PreviewTransportProps {
  player: PreviewPlayer;
  list: EditList;
  muted: boolean;
  onToggleMute: () => void;
  /** The stretch the creator has marked (output times), drawn on the strip. */
  selection: { fromMs: number | null; toMs: number | null };
  selectedSegmentId?: string | null;
  onSelectSegment?: (id: string) => void;
}

const SEGMENT_COLOURS = ['#0F766E', '#B45309', '#1D4ED8', '#7C3AED', '#BE185D', '#15803D'];

/** Play controls on the result's clock, and a strip of the kept stretches. */
const PreviewTransport: React.FC<PreviewTransportProps> = ({ player, list, muted, onToggleMute, selection, selectedSegmentId, onSelectSegment }) => {
  const placements = segmentPlacements(list);
  const total = Math.max(1, player.durationMs);
  return (
    <Box sx={{ bgcolor: '#111827', color: '#F9FAFB', px: 1.5, py: 1, borderRadius: '0 0 12px 12px' }}>
      <Box sx={{ position: 'relative', height: 22, mb: 0.5, borderRadius: 1, overflow: 'hidden', bgcolor: '#1F2937' }} data-testid="segment-strip">
        {placements.map(({ segment, startMs, endMs }, index) => (
          <Tooltip key={segment.id} title={`${formatMs(segment.inMs)} – ${formatMs(segment.outMs)} of the source${segment.speed !== 1 ? ` at ${segment.speed}×` : ''}`}>
            <Box
              component="button"
              type="button"
              aria-label={`Stretch ${index + 1}`}
              onClick={() => { onSelectSegment?.(segment.id); player.seek(startMs); }}
              sx={{
                position: 'absolute',
                left: `${(startMs / total) * 100}%`,
                width: `${Math.max(0.3, ((endMs - startMs) / total) * 100)}%`,
                top: 0,
                bottom: 0,
                border: 'none',
                borderRight: '1px solid #111827',
                bgcolor: SEGMENT_COLOURS[index % SEGMENT_COLOURS.length],
                opacity: selectedSegmentId && selectedSegmentId !== segment.id ? 0.45 : 0.9,
                cursor: 'pointer',
                p: 0,
              }}
            />
          </Tooltip>
        ))}
        {selection.fromMs !== null && selection.toMs !== null && selection.toMs > selection.fromMs ? (
          <Box data-testid="selection-band" sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(selection.fromMs / total) * 100}%`, width: `${((selection.toMs - selection.fromMs) / total) * 100}%`, border: '2px solid #FDE68A', pointerEvents: 'none' }} />
        ) : null}
        <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${(player.outputMs / total) * 100}%`, width: 2, bgcolor: '#F9FAFB', pointerEvents: 'none' }} />
      </Box>
      <Slider
        size="small"
        min={0}
        max={total}
        step={10}
        value={Math.min(total, player.outputMs)}
        onChange={(_, value) => player.seek(value as number)}
        aria-label="Position in the result"
        sx={{ color: '#F9FAFB', py: 0.5 }}
      />
      <Stack direction="row" alignItems="center" spacing={0.5}>
        <IconButton size="small" onClick={() => player.seek(player.outputMs - 5000)} aria-label="Back 5 seconds" sx={{ color: 'inherit' }}><Replay5Icon /></IconButton>
        <IconButton onClick={player.toggle} aria-label={player.playing ? 'Pause' : 'Play'} data-testid="preview-play" sx={{ color: 'inherit' }}>
          {player.playing ? <PauseIcon /> : <PlayArrowIcon />}
        </IconButton>
        <IconButton size="small" onClick={() => player.seek(player.outputMs + 5000)} aria-label="Forward 5 seconds" sx={{ color: 'inherit' }}><Forward5Icon /></IconButton>
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums', ml: 1 }} data-testid="preview-clock">
          {formatMs(player.outputMs)} / {formatMs(player.durationMs)}
        </Typography>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onToggleMute} aria-label={muted ? 'Unmute' : 'Mute'} sx={{ color: 'inherit' }}>
          {muted ? <VolumeOffIcon /> : <VolumeUpIcon />}
        </IconButton>
      </Stack>
    </Box>
  );
};

export default PreviewTransport;
